/**
 * ERPFormBridge — Integration layer between ERP and Form Builder
 * 
 * DOES NOT modify Form Builder components.
 * ONLY connects them to ERP APIs, auth, and routing.
 * 
 * SUPER_ADMIN: Can create/edit forms via AdvancedFormBuilder
 * All users: Can view/use forms via AdvancedGeneratedForm
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdvancedFormBuilder from './AdvancedFormBuilder';
import AdvancedGeneratedForm from './AdvancedGeneratedForm';
import { erpToast } from '../erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';


const ERPFormBridge = ({ theme = 'light' }) => {
  const [generatedForm, setGeneratedForm] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [formList, setFormList] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';

  // Load saved forms list from API
  const loadFormList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/formbuilder/list`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) setFormList(json.data || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { loadFormList(); }, [loadFormList]);

  // Load a specific form config
  const loadForm = async (formId) => {
    try {
      const res = await fetch(`${API_URL}/api/formbuilder/${formId}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success && json.data) {
        const config = typeof json.data.FormConfig === 'string' ? JSON.parse(json.data.FormConfig) : json.data.FormConfig;
        setGeneratedForm(config);
        setSelectedFormId(formId);
        setShowBuilder(false);
      }
    } catch { erpToast.error('Failed to load form'); }
  };

  // Save form config to API
  const handleFormCreated = async (formConfig) => {
    setGeneratedForm(formConfig);
    setShowBuilder(false);
    try {
      const method = selectedFormId ? 'PUT' : 'POST';
      const url = selectedFormId ? `${API_URL}/api/formbuilder/${selectedFormId}` : `${API_URL}/api/formbuilder`;
      await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ FormConfig: JSON.stringify(formConfig), FormName: formConfig.formName || `Form_${Date.now()}` })
      });
      erpToast.success('Form saved!');
      loadFormList();
    } catch { erpToast.error('Failed to save form'); }
  };

  const handleFormSubmit = (formData) => {
    console.log('ERP Form submitted:', formData);
    erpToast.success('Form submitted successfully!');
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await fetch(`${API_URL}/api/formbuilder/${formId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      erpToast.success('Form deleted');
      loadFormList();
      if (selectedFormId === formId) { setGeneratedForm(null); setSelectedFormId(null); }
    } catch { erpToast.error('Failed to delete'); }
  };

  return (
    <div className="erp-module" data-testid="erp-form-bridge">
      {/* Header */}
      <div className="erp-screen-header">
        <span className="erp-screen-title">Form Builder</span>
        <div className="erp-screen-actions">
          {isSuperAdmin && (
            <button className="erp-btn erp-btn-primary" onClick={() => { setShowBuilder(true); setSelectedFormId(null); setGeneratedForm(null); }}>
              + New Form
            </button>
          )}
          {generatedForm && isSuperAdmin && (
            <button className="erp-btn erp-btn-ghost" onClick={() => setShowBuilder(true)}>Edit Form</button>
          )}
          {generatedForm && (
            <button className="erp-btn erp-btn-ghost" onClick={() => { setGeneratedForm(null); setSelectedFormId(null); setShowBuilder(false); }}>Back to List</button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {showBuilder && isSuperAdmin ? (
          <AdvancedFormBuilder onFormCreated={handleFormCreated} initialForm={generatedForm} />
        ) : generatedForm ? (
          <div style={{ padding: 'var(--erp-sp-4)' }}>
            <AdvancedGeneratedForm formConfig={generatedForm} onSubmit={handleFormSubmit} />
          </div>
        ) : (
          /* Form List */
          <div style={{ padding: 'var(--erp-sp-4)' }}>
            <div className="erp-grid-wrapper">
              <table className="erp-grid">
                <thead>
                  <tr>
                    <th>Form ID</th>
                    <th>Form Name</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--erp-text-muted)' }}>Loading...</td></tr>
                  ) : formList.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--erp-text-muted)' }}>No forms created yet{isSuperAdmin ? ' — click "+ New Form" to start' : ''}</td></tr>
                  ) : formList.map(f => (
                    <tr key={f.Id}>
                      <td className="cell-mono">{f.Id}</td>
                      <td className="cell-bold" style={{ cursor: 'pointer', color: 'var(--erp-primary)' }} onClick={() => loadForm(f.Id)}>{f.FormName}</td>
                      <td className="cell-muted">{f.CreatedAt}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="erp-btn-icon view" onClick={() => loadForm(f.Id)} title="Open">Open</button>
                          {isSuperAdmin && <button className="erp-btn-icon delete" onClick={() => handleDeleteForm(f.Id)} title="Delete">Del</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERPFormBridge;
